import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Save, Sparkles, EyeOff, Eye } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";

const AVAILABLE_VARS = [
  "business_name", "contact_name", "first_name", "email", "phone",
  "service_address", "abn",
  "plan_name", "plan_monthly_price",
  "current_plan_name", "current_monthly_fee",
  "partner_code", "partner_name",
  "syncro_ticket_number",
  "tenant_brand", "today",
  "nbn_username", "nbn_password",
];

export default function AdminCannedResponses() {
  const [rows, setRows] = useState(null);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);

  async function load() {
    try {
      const { data } = await api.get("/commissions/canned-responses");
      setRows(data.templates || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load templates");
    }
  }
  useEffect(() => { load(); }, []);

  async function save() {
    if (!editing) return;
    setBusy(true);
    try {
      if (editing.id) {
        await api.put(`/commissions/canned-responses/${editing.id}`, editing);
      } else {
        await api.post("/commissions/canned-responses", editing);
      }
      toast.success("Saved");
      setEditing(null);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    } finally { setBusy(false); }
  }

  async function remove(id) {
    if (!window.confirm("Delete this template? Existing sent messages are unaffected.")) return;
    try {
      await api.delete(`/commissions/canned-responses/${id}`);
      toast.success("Deleted");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Delete failed");
    }
  }

  async function seedDefaults() {
    try {
      const { data } = await api.post("/commissions/canned-responses/seed-defaults");
      toast.success(data.added > 0 ? `Added ${data.added} starter templates` : "Starter templates already present");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Seed failed");
    }
  }

  function insertVar(v) {
    if (!editing) return;
    const suffix = `{{${v}}}`;
    setEditing(e => ({ ...e, body: (e.body || "") + suffix }));
  }

  return (
    <div className="space-y-5" data-testid="admin-canned-responses-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-3xl tracking-tight">Canned responses</h1>
          <p className="text-sm text-slate-400 mt-1 max-w-2xl">
            Reusable message templates for Syncro ticket comments. Use <code className="text-cyan-300">{"{{variable}}"}</code> placeholders — they auto-fill from the client record when you send. Perfect for NBN handover notices, status updates, and other stock messages.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            data-testid="canned-seed-defaults"
            onClick={seedDefaults}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-slate-800 hover:bg-slate-800 text-sm text-slate-200"
          >
            <Sparkles className="w-4 h-4" /> Add starter templates
          </button>
          <button
            data-testid="canned-new"
            onClick={() => setEditing({ name: "", subject: "", body: "", visibility: "hidden", tags: [] })}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-cyan-500 hover:bg-cyan-400 text-white text-sm font-medium"
          >
            <Plus className="w-4 h-4" /> New template
          </button>
        </div>
      </div>

      <div className="rounded-xl border border-slate-800 bg-slate-900 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-950/50 border-b border-slate-800 text-xs uppercase tracking-widest text-slate-500">
            <tr>
              <th className="text-left px-4 py-3 font-semibold">Name</th>
              <th className="text-left px-4 py-3 font-semibold">Visibility</th>
              <th className="text-left px-4 py-3 font-semibold">Tags</th>
              <th className="text-left px-4 py-3 font-semibold">Updated</th>
              <th className="w-24 px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {rows === null ? (
              <tr><td colSpan={5} className="px-4 py-10 text-center text-slate-500">Loading…</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={5} className="px-4 py-10 text-center text-slate-500">No templates yet — click &quot;Add starter templates&quot; for a starter set.</td></tr>
            ) : rows.map(r => (
              <tr key={r.id} className="border-b border-slate-800/70 hover:bg-slate-950/60 transition-colors">
                <td className="px-4 py-3">
                  <button onClick={() => setEditing({ ...r })} className="text-slate-100 font-medium hover:text-cyan-300 text-left" data-testid={`canned-row-${r.id}`}>
                    {r.name}
                  </button>
                  {r.subject && <div className="text-[11px] text-slate-500 truncate max-w-md">{r.subject}</div>}
                </td>
                <td className="px-4 py-3">
                  {r.visibility === "customer_facing" ? (
                    <span className="inline-flex items-center gap-1 text-[10px] bg-emerald-500/10 border border-emerald-500/40 text-emerald-300 rounded px-1.5 py-0.5 uppercase tracking-widest font-semibold">
                      <Eye className="w-2.5 h-2.5" /> Customer
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-[10px] bg-amber-500/10 border border-amber-500/40 text-amber-300 rounded px-1.5 py-0.5 uppercase tracking-widest font-semibold">
                      <EyeOff className="w-2.5 h-2.5" /> Internal
                    </span>
                  )}
                </td>
                <td className="px-4 py-3 text-[11px] text-slate-500">{(r.tags || []).join(", ") || "—"}</td>
                <td className="px-4 py-3 text-[11px] text-slate-500">{r.updated_at ? new Date(r.updated_at).toLocaleString(undefined, { month: "short", day: "2-digit" }) : "—"}</td>
                <td className="px-4 py-3 text-right">
                  <button onClick={() => remove(r.id)} className="text-red-400 hover:text-red-300" data-testid={`canned-delete-${r.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm p-6 overflow-y-auto" onClick={() => setEditing(null)}>
          <div className="max-w-3xl mx-auto bg-slate-900 border border-slate-800 rounded-xl p-6" onClick={(e) => e.stopPropagation()} data-testid="canned-edit-modal">
            <h3 className="font-display text-xl tracking-tight mb-4">{editing.id ? "Edit template" : "New template"}</h3>
            <div className="space-y-3">
              <div>
                <label className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Name (internal)</label>
                <input value={editing.name} onChange={(e) => setEditing({...editing, name: e.target.value})} className="w-full mt-1 bg-slate-950 border border-slate-800 focus:border-cyan-500 outline-none rounded-md px-3 py-2 text-sm" data-testid="canned-edit-name" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Visibility</label>
                <select value={editing.visibility} onChange={(e) => setEditing({...editing, visibility: e.target.value})} className="w-full mt-1 bg-slate-950 border border-slate-800 rounded-md px-3 py-2 text-sm" data-testid="canned-edit-visibility">
                  <option value="hidden">Internal (private Syncro comment)</option>
                  <option value="customer_facing">Customer-facing (email reply)</option>
                </select>
              </div>
              {editing.visibility === "customer_facing" && (
                <div>
                  <label className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Subject</label>
                  <input value={editing.subject || ""} onChange={(e) => setEditing({...editing, subject: e.target.value})} className="w-full mt-1 bg-slate-950 border border-slate-800 focus:border-cyan-500 outline-none rounded-md px-3 py-2 text-sm" placeholder="Your NBN service is live" />
                </div>
              )}
              <div>
                <label className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Body</label>
                <textarea rows={10} value={editing.body} onChange={(e) => setEditing({...editing, body: e.target.value})} className="w-full mt-1 bg-slate-950 border border-slate-800 focus:border-cyan-500 outline-none rounded-md px-3 py-2 text-sm font-mono" data-testid="canned-edit-body" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Insert variable</label>
                <div className="mt-1 flex flex-wrap gap-1.5">
                  {AVAILABLE_VARS.map(v => (
                    <button key={v} onClick={() => insertVar(v)} className="text-[11px] bg-slate-800 hover:bg-cyan-500/20 border border-slate-700 text-slate-300 hover:text-cyan-200 rounded px-2 py-1 font-mono">
                      {`{{${v}}}`}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Tags (comma-separated)</label>
                <input value={(editing.tags || []).join(", ")} onChange={(e) => setEditing({...editing, tags: e.target.value.split(",").map(s => s.trim()).filter(Boolean)})} className="w-full mt-1 bg-slate-950 border border-slate-800 focus:border-cyan-500 outline-none rounded-md px-3 py-2 text-sm" placeholder="nbn, handover, status" />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button onClick={() => setEditing(null)} className="px-3 py-2 text-sm rounded-lg border border-slate-800 hover:bg-slate-800">Cancel</button>
              <button onClick={save} disabled={busy} data-testid="canned-edit-save" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-cyan-500 hover:bg-cyan-400 text-white text-sm font-medium disabled:opacity-60">
                <Save className="w-4 h-4" /> {busy ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
